<script>
   /*************************************************************/
   /* AppContainer for predictions                              */
   /* -------------------------                                 */
   /* app container for applying PLS1DA model to new set        */
   /*************************************************************/

   import { Button, Container } from 'svelte-controls-basic';
   import { plsmodel_get, plsres_get } from '@mdatools-apps/wasm/pls.js';
   import { app_residuals } from '@mdatools-apps/shared/utils';
   import { downloadResults, predict } from '../utils';

   import Outliers from '@mdatools-apps/shared/widgets/Outliers.svelte';
   import Contributions from '@mdatools-apps/shared/components/Contributions.svelte';

   import DataLoader from '../shared/DataLoader.svelte';
   import ModelLoader from '../shared/ModelLoader.svelte';

   import AppPlots from './AppPlots.svelte';

   // WASM promise and session object from parent app
   let {WASM, backupSession, session = $bindable()} = $props();

   // get part of the session for this tab
   const s = $derived(session['new']);

   // properties for showing contributions
   let selected = $state(-1);
   let contributionIsShown = $state(false);


   // callback function to set object with a give position in plot series
   // as outlier
   function markOutlier(pos) {

      // check that position is valid
      if (pos < 0 || pos > s.newres.pls.nrows - 1) return;

      // find sample ID corresponding to the position
      // this is needed if some of the objects already
      // removed from results as outliers
      const id = s.newres.pls.objindices[pos];

      // add the object to outliers array and reset the model
      if (id >= 0 && !s.outliers.includes(id)) {
         s.outliers.push(id);
         s.newres = {pls: null, da: null};
      }
   }


   // callback to reset outliers
   function resetOutliers() {
      s.outliers = [];
      s.newres = {pls: null, da: null};
   }

   // callback to undo outliers
   function undoOutliers() {
      s.outliers.pop();
      s.newres = {pls: null, da: null};
   }

   // show modal dialog with contributions for selected object
   function showContributions(pos) {
      if (pos < 0) return;
      selected = pos;
      contributionIsShown = true;
   }

   // hide modal dialog with contributions
   function hideContributions() {
      contributionIsShown = false;
      selected = -1;
   }



   // effect to track changes in dataset and models
   $effect(() => {

      // if new set is set null - reset results and all related parameters
      if (s.newset === null) {
         s.outliers = [];
         s.newres = {pls: null, da: null};
      }

      // if model object is set to null - reset dependent objects
      if (s.model === null) {
         s.newres = {pls: null, da: null};
         s.newset = null;
      }
   });

   // recalculate DA results for calibration set when DA parameters have changed
   $effect(() => {
      if (s.newres.pls && s.model.da) {
         s.newres.da = predict(s.newres.pls, s.model.da, null)
      }
   });

   // reactive effect for backing up the session. the session is read by 'backupSession' when the
   // write is due, not here, and the first run is skipped: it only ever coincides with a session
   // the app was given as a whole, which is already on disk, is written by 'MainApp' itself, or is
   // a fresh one that need not be stored. the values after the function are the dependencies (see
   // 'backupSession')
   let mounted = false;
   $effect(() => {
      const deps = [s.model, s.newres.da];
      if (mounted) backupSession(() => session, ...deps);
      mounted = true;
   });
</script>

<div class="app-container">

   {#if contributionIsShown}
   <Contributions
      model_getter={plsmodel_get}
      res_getter={plsres_get}
      model={s.model.pls}
      res={{new: s.newres.pls}}
      residuals={() => WASM().then((M) =>
         app_residuals(M, s.model.pls, s.newset, s.newres.pls, true))}
      ncomp={s.model.da.ncomp}
      {selected}
      closeModal={hideContributions}
   />
   {/if}

   <!-- left column with main app controls -->
   <div class="app-controls">
      <div class="model-loader">
         <ModelLoader bind:model={s.model} />
      </div>

      {#if s.model}
      <div class="data-loader">
         <DataLoader
            {WASM}
            bind:dataset={s.newset}
            bind:resobj={s.newres.pls}
            outliers={s.outliers}
            model={s.model.pls}
            classNames={s.model.da?.classNames ?? null}
            classMandatory={false}
         />
      </div>
      {/if}

      {#if s.outliers && s.outliers.length > 0 }
      <div class="outliers-pane">
         <Outliers outliers={s.outliers} dataset={s.newset} {resetOutliers} {undoOutliers} />
      </div>
      {/if}


      {#if s.newres.da}
      <div class="ncomp-selector">
         <Container label='Download'>
            <div class="buttons">
            <Button onclick={() => downloadResults(s.newres, s.newset, "new", s.model.da.ncomp)} text="Res (new)"/>
            </div>
         </Container>
      </div>
      {/if}
   </div>

   <!-- right column with help text or with plots and plot controls -->
   <div class="app-content">
      {#if !s.newres.da}
         <div class="help-text">
         {#if !s.model}
            <p>
               In this tab you can apply a PLS1-DA classification model developed in <strong>Train</strong> tab to
               new set of measurements without known class labels.
            </p>
            <p><strong>Step 1.</strong></p>
            <p>Load the previously developed PLS1-DA model from a JSON file.</p>
         {:else if !s.newset}
            <p><strong>Step 2.</strong></p>
            <p>Load your new set. In this case, because dataset should not contain the class labels, you
            can load it either from a CSV file or from SPC files.</p>
            <p>If you load data from a <strong>CSV file</strong>, the file structure should be the same as the training/calibration set but
               <strong>without the column with class labels</strong> (only predictors). Here is an example how data structure may look like
               (note, that row labels and header are optional):
            </p>
            <div>
               <table>
                  <thead>
                  <tr>
                     <td>Name</td>
                     <td>x1</td>
                     <td>x2</td>
                     <td>x3</td>
                  </tr>
                  </thead>
                  <tbody>
                  <tr>
                     <td>O1</td>
                     <td>3.8</td>
                     <td>1.3</td>
                     <td>13.6</td>
                  </tr>
                  <tr>
                     <td>O2</td>
                     <td>11.1</td>
                     <td>12.3</td>
                     <td>3.3</td>
                  </tr>
                  <tr>
                     <td>O3</td>
                     <td>1.2</td>
                     <td>2.3</td>
                     <td>4.2</td>
                  </tr>
                  </tbody>
               </table>
               <p>If your model uses spectra as predictors, you can also load spectral data directly
                  from Galactic Industries <strong>SPC files</strong>. The app supports both single spectrum in a file and
                  files with multiple spectra, however only 2-way datasets can be loaded. You can select several
                  SPC files to load (hold Ctrl or Shift when selecting), in this case they will be combined together
                  into the same dataset.

               </p>
            </div>
            <p></p>

         {/if}
         </div>
      {:else}

         <!-- keyed on the model and not only guarded by it: a session file whose date equals the
              open session's, which is the same file loaded a second time, leaves the tabs mounted
              while every field is swapped for a new object at once, and the plot container
              captures its controls when it mounts -->
         {#key s.newres && s.model }
         {#if s.newres && s.newres.da && s.model && s.model.da}
         <AppPlots
            {markOutlier}  {showContributions}
            data={{'new': s.newset}}
            res={{'new': s.newres}}
            model={s.model}
         />
         {/if}
         {/key}
      {/if}
   </div>
</div>

<style>

   .app-container {
      font-family: Helvetica, Arial, sans-serif;
      font-size: 1rem;
      color: #404040;
      box-sizing: border-box;
      margin: 0 auto;
      padding: 0.35em 0.25em;
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-areas: "controls content";
      grid-template-rows: 100%;
      grid-template-columns: min(310px, 30%) 1fr;
   }

   .app-controls {
      box-sizing: border-box;
      padding: 0.25em 0.5em;
   }

   .app-content {
      box-sizing: border-box;
      padding: 0.25em 0.25em;
      height: 100%;
   }

   .app-controls > div {
      border-radius: 0.25rem;
   }

   .help-text {
      margin-top: 0.25rem;
   }

   .help-text > p, .help-text > div {
      background: #fafafa;
      padding: 0em 1.5em 0.5em 1.5em;
      color: #606060;
      line-height: 1.5em;
      margin: 0 1em 0 0;
   }

   .help-text table {
      border-collapse: collapse;
   }

   .help-text table td{
      padding: 0.25em 0.5em;
      background: #e0f0fa;
      text-align: right;
      border: 1px solid white;
   }

   .help-text table thead tr td{
      font-weight: bold;
      background: #f8f4e0;
   }

   .help-text table td:first-child {
      font-weight: bold;
      background: #f8f4e0;
      padding-right: 1em;
   }


   .help-text > p:first-child {
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      padding-top: 1.5em;
   }

   .help-text > p:last-child {
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
      padding-bottom: 1.5em;
   }


   @media(min-width:1100px) {
      .app-container {
         grid-template-columns: min(330px, 30%) 1fr;
      }
   }

   @media(min-width:1300px) {
      .app-container {
         grid-template-columns: min(340px, 30%) 1fr;
      }
   }

</style>